import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { Grade, GradeStatus } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import * as gradeActions from '../../../../state/actions/grade.school.actions';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { DlgAssignFacultyComponent } from '../../dlg-assign-faculty/dlg-assign-faculty.component';
import { DlgUpsertGradeComponent } from '../dlg-upsert-grade/dlg-upsert-grade.component';

@Component({
  selector: 'app-list-grades',
  standalone: true,
  imports: [
    AsyncPipe,
    InitialsPipe,
    RouterLink,
    TitleCasePipe,
    MatDialogModule,
    BtnBackComponent,
    BtnChipComponent,
    BtnMoreComponent,
    BtnActionComponent,
    BtnFabComponent,
    ListUtilsBarComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './list-grades.component.html',
  styleUrl: './list-grades.component.css',
})
export class ListGradesComponent implements OnInit, OnDestroy {
  activeDropdownGradeId: string | null = null;
  route: ActivatedRoute = inject(ActivatedRoute);
  toastr: ToastrService = inject(ToastrService);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private dialog: MatDialog = inject(MatDialog);

  headers: string[] = ['Grade', 'Faculty', 'Status', 'Actions'];

  grades$: Observable<Grade[] | null | undefined> = new Observable();

  faculty: SchoolMember[] | null | undefined;

  loading$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  group: Group | null | undefined;

  membershipStatus = MembershipStatus;
  gradeStatus = GradeStatus;
  rolesType = SchoolRoles;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject();
  searchTerm: string | undefined;

  constructor(private store: Store<AppState>) {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
    });

    this.loading$ = this.store.select(gradeSelector.loading);

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.grades$ = this.store.pipe(
      select(gradeSelector.getGradesByGroup(this.groupId)),
    );
  }

  ngOnInit(): void {
    if (this.groupId) {
      this.store
        .select(groupSelector.getSelectedGroup)
        .pipe(takeUntil(this.exit$))
        .subscribe((group) => {
          if (group == undefined) return;
          this.group = group;
        });
    }

    this.store
      .select(
        memberSelector.getGroupMembersByType({
          groupId: this.groupId!,
          memberType: [SchoolMemberType.teacher],
          status: [MembershipStatus.active],
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((faculty) => {
        this.faculty = faculty;
        if (faculty?.length === 0) {
          this.store.dispatch(
            memberActions.getMembers({
              source: 'ListGradesComponent :: ngOnInit',
              groupId: this.groupId!,
              getMemberType: [SchoolMemberType.teacher],
            }),
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  addMore() {
    this.grades$.pipe(take(1)).subscribe((grades) => {
      this.store.dispatch(
        gradeActions.getGrades({
          groupId: this.groupId!,
          academicYear: this.group?.academicYear!,
          // grades are ordered by gradeAvgAge desc: page on that field
          lastRecord:
            (grades ?? [])?.length > 0
              ? grades![grades!.length - 1]?.gradeAvgAge
              : undefined,
        }),
      );
    });
  }

  onAddGrade() {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      groupId: this.groupId,
      academicYear: this.group?.academicYear,
      faculty: this.faculty,
    };

    const dialogRef = this.dialog.open(DlgUpsertGradeComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe(() => {
      return;
    });
  }

  onEditGrade(item: Grade) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      groupId: this.groupId,
      grade: item,
      faculty: this.faculty,
    };

    const dialogRef = this.dialog.open(DlgUpsertGradeComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success(
          `Grade '${item.gradeName}' updated successfully`,
          'Grade Updated',
        );
      }
      return;
    });
  }

  onAssignFaculty(item: Grade) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      groupId: this.groupId,
      grade: item,
      selectedOption: item?.teacherId,
    };

    const dialogRef = this.dialog.open(DlgAssignFacultyComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success(
          `Grade '${item.gradeName}' reassigned successfully`,
          'Grade Reassigned',
        );
      }
      return;
    });
  }

  onArchiveGrade(item: Grade) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Archive Grade',
      description: `Are you sure you want to archive grade '${item.gradeName}'?`,
      action: 'Archive',
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(
          gradeActions.update({
            id: item.id!,
            grade: { ...item, gradeStatus: GradeStatus.archive },
          }),
        );
      }
      return;
    });
  }

  onDeleteGrade(item: Grade) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Delete Grade',
      description: `If the grade has been used for sessions, then kindly archive instead of deletion.<br><br>Are you sure you want to delete grade '${item.gradeName}'?`,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      // showType: 'warning',
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        return this.store.dispatch(
          gradeActions.deleteAction({ groupId: item.groupId, id: item.id! }),
        );
      }
      return;
    });
  }

  onRefresh() {
    if (this.group?.academicYear)
      this.store.dispatch(
        gradeActions.getGrades({
          groupId: this.groupId!,
          academicYear: this.group?.academicYear!,
        }),
      );
  }

  async onSearchTermChange(value: string) {
    console.log('search term:', value);
    this.searchTerm = value;
  }

  toggleDropdown(id: string, event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (this.activeDropdownGradeId === id) {
      this.activeDropdownGradeId = null;
    } else {
      this.activeDropdownGradeId = id;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.activeDropdownGradeId = null;
  }
}
